import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "w-full" }

import { EventInput } from '@fullcalendar/core';
import { useI18n } from 'vue-i18n';
import EsWeeklyCalendar from '@/components/calendar/esWeeklyCalendar.vue';
import EsDailyCalendar from '@/components/calendar/esDailyCalendar.vue';
import EsCalendarSettings from '@/components/modals/calendar/esCalendarSettings.vue';
import Tabs from "@/components/tabs/tabs.vue";
import Tab from "@/components/tabs/tab.vue";
import { CalendarView } from '@/models/calendar.model';
import { ref, computed, watch, Ref, onMounted } from 'vue';
import { addDays, format } from "date-fns";
import { useAppUserStore } from '@/stores/appUser';
import { ApiError, AppointmentRequest, PlanningApi, SpecificDaySinceDayModelRequest, SettingsApi, CheckAppointmentResponse, PatientLightResponse } from '@/apiGenerate/medserver';
import { medServerApiConfig } from '@/config/medserver-api.config';
import DateService from '@/services/DateService';
import EsCalendarForm from "@/components/form/esCalendarForm.vue";
import { DateClickArg } from '@fullcalendar/interaction';
import SharedService from '@/services/SharedService';
import { DateFormat, ItemSelectPatient, ItemTag } from '@/models/share.model';
import { useCurrentEntryStore } from '@/stores/currentEntry';
import { toastError, toastSuccess } from '@/utils/toast-utils';
import { useToast } from 'primevue';
import { useWaitingRoomsStore } from '@/stores/waitingRooms';
import { PlanningRefreshSource } from '@/views/planningMain/enumPlanningRefreshSource';
import { getLocaleForDateFns } from '@/utils/date-utils';
import { getAppointmentEventColor } from '@/utils/appointment-color-utils';
import EsConfirmDialog from '@/components/modals/esConfirmDialog.vue';
import PersonService from "@/services/PersonService";


export default /*@__PURE__*/_defineComponent({
  __name: 'esCalendar',
  props: {
    selectedDate: {},
    currentView: {},
    doctors: {},
    refresh: { type: Boolean },
    changeDate: {},
    changeAgendaType: {},
    changeDoctor: {},
    changeDoctorList: {},
    lastSelectedDoctorId: {}
  },
  emits: ['updateSelectedDate', 'prev', 'next', 'changeCurrentView', 'switchOpened', 'refreshed', 'refreshCalendar', 'saveLastDoctorOpened', 'delete-appointment', 'today', 'updateDoctors'],
  setup(__props: any, { emit: __emit }) {

const planningApi = new PlanningApi(medServerApiConfig);

const props = __props;

const { t } = useI18n();
const toast = useToast();

const appUserStore = useAppUserStore();
const currentEntryStore = useCurrentEntryStore();
const waitingRoomsStore = useWaitingRoomsStore();

const settingsApi = new SettingsApi(medServerApiConfig);

const isSettingOpened = ref<boolean>(false);
const dateForSetting = ref<string>(null);
const isFormModalOpened = ref<boolean>(false);
const isConfirmAppointmentOpened = ref<boolean>(false);
const saveAppointmentRequest = ref<AppointmentRequest>(null);
const headerForm = ref<string>('calendar.label.add.header');
const displayDelete = ref<boolean>(false);
const changeAppointmentType = ref<string>('');
const changePatient = ref<string>('');
const activeTab = ref<number>(appUserStore.getCurrentAgendaType() ? Number.parseInt(appUserStore.getCurrentAgendaType()) : 1);
const isAllDayEvent = ref<boolean>(false);
const selectedDoctorAppoitmentTypes = ref<Record<string, ItemTag>>({});

const getCurrentEvent = computed (() => { return currentEntryStore.currentEvent; });

const getIsMoveAppointmentAction = computed (() => { return currentEntryStore.isMoveAppointmentAction; });

const getAppointmentId =  computed (() => { return currentEntryStore.appointmentId; });

const doctorsFiltered = computed(() => props.doctors.filter((doctor) => doctor.selected));

const getCurrentPatient = (): ItemSelectPatient => {
    return currentEntryStore.currentPatientCalendar
            ? {id: currentEntryStore.currentPatientCalendar.personWithRoleId, 
              label: PersonService.formatName(currentEntryStore.currentPatientCalendar.lastName, currentEntryStore.currentPatientCalendar.firstName),
              birthdate: currentEntryStore.currentPatientCalendar.birthDate?.split("T")[0].replaceAll("-", " "),
              serialNumber: currentEntryStore.currentPatientCalendar.cnsHealthInsurance?.cnsAffiliationNumber
              }
            : undefined;
}

const getBusinessHour = ref<boolean>(getCurrentPatient() == undefined || selectedDoctorAppoitmentTypes.value != null );
const addAppointmentDate = ref(new Date());

const selectedDoctor = ref<ItemTag>(null);

const emits = __emit;

const currInstituteUuid = computed(() => {
  if (!appUserStore.currentHealthGroupMembership) {
    return "";
  }
  const currentIwr = appUserStore.currentHealthGroupMembership.instituteWithRole;
  return currentIwr.instituteWithRoleId;
});

const local = computed(() => {
  return getLocaleForDateFns(appUserStore?.currentAppUserData?.preferences?.menuLanguageIso);
});

const setEventColor = (event: EventInput) => {
  if (event.extendedProps?.status) {
    const { backgroundColor, borderColor, textColor, trimColor } = getAppointmentEventColor(
      event.extendedProps.color,
      event.extendedProps.status,
      event.start,
      event.end
    );
    event.backgroundColor = backgroundColor;
    event.borderColor = borderColor;
    event.textColor = textColor;
    event.extendedProps.trimColor = trimColor;
  } else {
    event.display = 'background';
  }
}

const getEvents = async () => {
  const doctorsUuid = props.doctors.filter((doctor) => doctor.selected && doctor.opened).map((doctor) => doctor.id);

  if (currInstituteUuid.value && doctorsUuid && doctorsUuid.length === 1 && props.currentView === CalendarView.WEEK) {
    const periodStart = format( DateService.getFirstDayOfWeek(props.selectedDate, true), DateFormat.ISO_SMALL);
    const periodEnd = format( DateService.getLastDayOfWeek(props.selectedDate, true), DateFormat.ISO_SMALL);

    const events: EventInput[] = await planningApi.listAppointmentsByPeriod({
      instituteId: currInstituteUuid.value,
      periodStart: periodStart,
      periodEnd: periodEnd,
      doctor: doctorsUuid[0],
      appointmentType: selectedDoctorAppoitmentTypes.value[doctorsUuid[0]] ? selectedDoctorAppoitmentTypes.value[doctorsUuid[0]].id :  null,
      displayPeriodPast: !!selectedDoctorAppoitmentTypes.value || getBusinessHour.value
    });
    
    events.map(event => event.allDay = event.isPatientWithoutAppointment);

    events.forEach((event: EventInput) => setEventColor(event));
    return events;
  }
};

const getEventByDay = async (doctorUuid: string) => {
  if (currInstituteUuid.value && doctorUuid && props.currentView === CalendarView.DAY) {

    const periodStart = format(props.selectedDate, DateFormat.ISO_SMALL);
    const periodEnd = format(addDays(new Date(props.selectedDate), 1), DateFormat.ISO_SMALL);

    const events: EventInput[] = await planningApi.listAppointmentsByPeriod({
      instituteId: currInstituteUuid.value,
      periodStart: periodStart,
      periodEnd: periodEnd,
      doctor: doctorUuid,
      appointmentType: selectedDoctorAppoitmentTypes.value[doctorUuid] ? selectedDoctorAppoitmentTypes.value[doctorUuid].id :  null,
      displayPeriodPast: !!selectedDoctorAppoitmentTypes.value || getBusinessHour.value
    });
  
    events.forEach((event: EventInput) => setEventColor(event));
    return events;
  }
}

const refreshEvents = (tabIndex: number | undefined, refreshValue: boolean, refreshCalendar: boolean) => {
  if (tabIndex != undefined) {
    tabIndex === 1 ? emits('changeCurrentView', CalendarView.WEEK) : emits('changeCurrentView', CalendarView.DAY);
  }
  emits('refreshed', { newValue: refreshValue, newValueCalendar: refreshCalendar });
}

const previous = () => {  
  emits('prev', props.selectedDate);
}

const next = () => {
  emits('next', props.selectedDate);
}

const updateRefreshAutoScrollVariable = (refreshVariable : Ref<string, string>, baseRefreshValue: string) => {
  if (refreshVariable.value !== baseRefreshValue) {
    refreshVariable.value = baseRefreshValue; 
  } else {
    refreshVariable.value = ' ' + baseRefreshValue; 
  }
}

const selectAppointmentType = (data: {appointmentType: ItemTag, doctorId: string}) => {
  if (data.doctorId) {
    selectedDoctorAppoitmentTypes.value[data.doctorId] = data.appointmentType;
  }
  
  updateRefreshAutoScrollVariable(changeAppointmentType, PlanningRefreshSource.AppointmentTypeChange);

  getBusinessHour.value = data.appointmentType == null || !!getCurrentPatient();
  emits('refreshed', {newValue: true, newValueCalendar: true});
}

const patientTagClick = () => {
  updateRefreshAutoScrollVariable(changePatient, PlanningRefreshSource.PatientChange);
  selectedDoctorAppoitmentTypes.value = {};
  getBusinessHour.value = true;
  currentEntryStore.setIsMoveAppointmentAction(false);
  currentEntryStore.setMoveAppointmentFromCalendar(undefined);
  currentEntryStore.setMoveAppointmentFromPatient(undefined, undefined);
  currentEntryStore.setCurrentEvent(undefined);
  currentEntryStore.setAppointmentId(undefined);
  currentEntryStore.setCurrentPatientCalendar(undefined);
  saveAppointmentRequest.value = undefined;
  emits('refreshed', {newValue: true, newValueCalendar: true});
}

const openAddForm = async (data : {info: DateClickArg, item: ItemTag}) => {
  if (data.info && data.info.date) {
    isAllDayEvent.value = data.info.allDay;
    headerForm.value = isAllDayEvent.value ? t('calendar.label.add.patientWithoutAppointmentHeader') : getIsMoveAppointmentAction.value ? t('calendar.label.move.action') : t('calendar.label.add.header') ;
    addAppointmentDate.value = data.info.date;
    selectedDoctor.value = data.item;
    isFormModalOpened.value = true;
    displayDelete.value = false;
  }
}

const moveAppointment = async (data: {doctorId: string, selectedPatient: PatientLightResponse,  selectedType: ItemTag, appointment: EventInput, appointmentId: string}) => {
  if (data.selectedPatient && data.doctorId && data.selectedType) {
    currentEntryStore.setCurrentPatientCalendar({
      personWithRoleId: data.selectedPatient.patientId,
      firstName: data.selectedPatient.firstname,
      lastName: data.selectedPatient.lastname,
      birthDate: data.selectedPatient.birthdate,
      cnsHealthInsurance: {
        cnsAffiliationNumber: data.selectedPatient.serialNumber
      }});

    selectAppointmentType({appointmentType: data.selectedType, doctorId: data.doctorId });

    currentEntryStore.setIsMoveAppointmentAction(true);

    if(data.appointment) {
      currentEntryStore.setCurrentEvent(data.appointment);
    }

    props.doctors.forEach((doctor: ItemTag) => {
      doctor.selected = doctor.id === data.doctorId;
    });
    emits('updateDoctors', props.doctors);
  }
  isFormModalOpened.value = false;
}

const closeFormModal = async (appointmentRequest: AppointmentRequest) => {
    if (appointmentRequest) {
      if(appointmentRequest.patientId === undefined && appointmentRequest.personAppointment?.firstname === undefined && appointmentRequest.personAppointment?.lastname === undefined && appointmentRequest.portalUserUuid === undefined){
        toastError(toast, {
              summary: t('common.toast.error'),
              detail: t(`error.NO_PATIENT`)
        });
        // Throw error to set at false the var __alreadyClicked in v-one-click directive
        throw t(`error.NO_PATIENT`);
      }

      saveAppointmentRequest.value = SharedService.cleanObject(appointmentRequest);
      
      await planningApi.checkAppointmentExist({instituteId: currInstituteUuid.value, doctorId: saveAppointmentRequest.value.doctorId, 
                                               appointmentDate: saveAppointmentRequest.value.date, appointmentTime: saveAppointmentRequest.value.time,
                                               duration: saveAppointmentRequest.value.duration, appointmentId: getAppointmentId.value ?? null})
        .then(async (checkAppointmentResponse: CheckAppointmentResponse) => {
          if (checkAppointmentResponse.alreadyExist) {
            isConfirmAppointmentOpened.value = true;
          } else {
              if (getAppointmentId.value) {
                await updateAppointment(saveAppointmentRequest.value);                
              } else {
                await createAppointment(saveAppointmentRequest.value);
              }
          }
        }).catch((error: ApiError) => {
          toastError(toast, {
              summary: t('common.toast.error'),
              detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
          });
          // Throw error to set at false the var __alreadyClicked in v-one-click directive
          throw error;
        });
    } else {
      isFormModalOpened.value = false;
    }
}

const initAppointmentFormModal = (appointmentRequest: AppointmentRequest) => {
  isFormModalOpened.value = false;
  selectedDoctorAppoitmentTypes.value = {};
  saveAppointmentRequest.value = undefined;

  currentEntryStore.setAppointmentId(undefined);
  currentEntryStore.setIsMoveAppointmentAction(false);
  currentEntryStore.setMoveAppointmentFromCalendar(undefined);
  currentEntryStore.setMoveAppointmentFromPatient(undefined, undefined);
  currentEntryStore.setCurrentEvent(undefined);

  refreshEvents(undefined, !!appointmentRequest, !!appointmentRequest);
}

const closeConfirmModal = async (continueCreate: boolean) => {
  if (continueCreate) {
    if (getAppointmentId.value) {
      await updateAppointment(saveAppointmentRequest.value);
    } else {
      await createAppointment(saveAppointmentRequest.value);
    }    
  } else {
    isFormModalOpened.value = false;
    initAppointmentFormModal(saveAppointmentRequest.value);
  }
  isConfirmAppointmentOpened.value = false;
}

const closeModal = () => {
  isFormModalOpened.value = false;
  if (!getIsMoveAppointmentAction.value) {
    initAppointmentFormModal(undefined);
  }  
}

const updateAppointment = async (appointmentRequest: AppointmentRequest) => {
  await planningApi.updateAppointment({
    instituteId: currInstituteUuid.value,
    appointmentId: getAppointmentId.value,
    appointmentRequest: appointmentRequest,
    isMove: getIsMoveAppointmentAction.value
  }).then(() => {
    toastSuccess(toast, {
        summary: t('common.toast.success.update'),
        detail:  t('calendar.label.message.success.update')
    });
    isFormModalOpened.value = false;
    initAppointmentFormModal(saveAppointmentRequest.value);
    waitingRoomsStore.updateWaitingRooms();
  }).catch((error: ApiError) => {
    toastError(toast, {
        summary: t('common.toast.error'),
        detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
    });
    // Throw error to set at false the var __alreadyClicked in v-one-click directive
    throw error;
  });
}

const createAppointment = (appointmentRequest: AppointmentRequest) => {
  planningApi.addAppointment({
    instituteId: currInstituteUuid.value,
    appointmentRequest: appointmentRequest}).then(async () => {

      await currentEntryStore.setCurrentPatientCalendar(null);      
      toastSuccess(toast, {
        summary: t('common.toast.success.add'),
        detail:  t('calendar.label.message.success.add')
      });
      isFormModalOpened.value = false;
      initAppointmentFormModal(appointmentRequest);
    }).catch((error: ApiError) => {
      toastError(toast, {
        summary: t('common.toast.error'),
        detail: t(`error.${error.status}`)
      });
      // Throw error to set at false the var __alreadyClicked in v-one-click directive
      throw error;
  });
}

const deleteAppointment = async () => {
  await planningApi.deleteAppointment({
    instituteId: currInstituteUuid.value,
    appointmentId: getAppointmentId.value,
  }).then(() => {
    isFormModalOpened.value = false;
    initAppointmentFormModal(undefined);
    refreshEvents(undefined, true, false);
    toastSuccess(toast, {
      summary: t('common.toast.success.cancel'),
      detail:  t('calendar.appointment.delete.message.success')
    });
  }).catch((error: ApiError) => {
    toastError(toast, {
        summary: t('common.toast.error'),
        detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
    });

    // Throw error to set at false the var __alreadyClicked in v-one-click directive
    throw error;
  });
}

const closeCalendarSettingsModal = (data: {specificDaySinceDayModelRequest: SpecificDaySinceDayModelRequest, dayModelId: string}) => {
  if (data && data.specificDaySinceDayModelRequest && currInstituteUuid.value && selectedDoctor.value?.id &&  dateForSetting.value) {

    if (data.dayModelId) {
      settingsApi.updateSpecificDaySinceNewDayModel({instituteId: currInstituteUuid.value,  specificDaySinceDayModelRequest: data.specificDaySinceDayModelRequest, daymodelId: data.dayModelId}).then(() => {
          isSettingOpened.value = false;
          toastSuccess(toast, {
              summary: t('common.toast.success.update'),
              detail:  t('settings.label.specialDays.toast.updated')
            });
            emits('refreshCalendar');
        }).catch((error: ApiError) => {
          toastError(toast, {
              summary: t('common.toast.error'),
              detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
          });

          // Throw error to set at false the var __alreadyClicked in v-one-click directive
          throw error;
        });
    } else {
       settingsApi.addSpecificDaySinceNewDayModel({instituteId: currInstituteUuid.value,  specificDaySinceDayModelRequest: data.specificDaySinceDayModelRequest,
        doctorId: selectedDoctor.value.id, date: dateForSetting.value}).then(() => {
          isSettingOpened.value = false;
          toastSuccess(toast, {
              summary: t('common.toast.success.update'),
              detail:  t('settings.label.specialDays.toast.created')
            });
            emits('refreshCalendar');
        }).catch((error: ApiError) => {
          toastError(toast, {
              summary: t('common.toast.error'),
              detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
          });

          // Throw error to set at false the var __alreadyClicked in v-one-click directive
          throw error;
        });
    }
  } else {
    isSettingOpened.value = false;
  }
}

const openUpdateEvent = async (eventToUpdate :{info: EventInput, item: ItemTag}) => {
  isAllDayEvent.value = eventToUpdate.info.event.allDay;
  headerForm.value = t('calendar.label.update.header');
  currentEntryStore.setCurrentEvent(eventToUpdate.info.event);
  selectedDoctor.value = await props.doctors.filter((doctor) => doctor.id === eventToUpdate.item.id)[0];
  currentEntryStore.setAppointmentId(eventToUpdate.info.event.extendedProps.appointmentId);
  isFormModalOpened.value = true;
  displayDelete.value = true;
}

const openSettings = (dataToParam :{date: Date, doctorId: string}) => {
  dateForSetting.value = format(dataToParam.date, DateFormat.ISO_SMALL), DateFormat.FRONT_LONG;
  selectedDoctor.value = props.doctors.filter((doctor) => doctor.id === dataToParam.doctorId)[0];
  isSettingOpened.value = true;
}

watch(
  () => currentEntryStore.currentPatientCalendar,
  () => {
    if (!currentEntryStore.currentPatientCalendar)  {
      getBusinessHour.value = true;
    } else {
      getBusinessHour.value = selectedDoctorAppoitmentTypes.value != null;
    }
  }
);

onMounted(async () => {
    if (currentEntryStore.moveAppointmentFromPatient) {
      currentEntryStore.setAppointmentId(currentEntryStore.moveAppointmentFromPatient.uuid);

      const appointmentType: ItemTag = {
        id: currentEntryStore.moveAppointmentFromPatient.typeId,
        label: currentEntryStore.moveAppointmentFromPatient.type,
        opened: true,
        selected: true
      }

      await moveAppointment({doctorId: currentEntryStore.moveAppointmentFromPatient.healthProId, selectedPatient: currentEntryStore.patientLightResponse,  selectedType: appointmentType, appointment: undefined, appointmentId: currentEntryStore.moveAppointmentFromPatient.uuid})
    }
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(Tabs, {
        titles: [_unref(t)('calendar.header.tab.day'), _unref(t)('calendar.header.tab.week')],
        showCalendarHeader: true,
        "selected-date": _ctx.selectedDate,
        onChangeTab: _cache[16] || (_cache[16] = ($event: any) => (refreshEvents($event, true, true))),
        onLeftNavigation: _cache[17] || (_cache[17] = ($event: any) => (previous())),
        onRightNavigation: _cache[18] || (_cache[18] = ($event: any) => (next())),
        onRedirectToday: _cache[19] || (_cache[19] = ($event: any) => (emits('today', new Date())))
      }, {
        default: _withCtx(() => [
          _createVNode(Tab, {
            active: activeTab.value === 0,
            class: "mtop-08"
          }, {
            default: _withCtx(() => [
              (_unref(appUserStore).reactiveCurrentAgendaType === '0')
                ? (_openBlock(), _createBlock(EsDailyCalendar, {
                    key: 0,
                    customRef: 'fullCalendarDay',
                    itemTags: doctorsFiltered.value,
                    selectedDate: _ctx.selectedDate,
                    getEventByDay: getEventByDay,
                    refresh: _ctx.refresh,
                    smallDisplay: false,
                    calcHeight: 200,
                    cssEventClass: 'pleft-04',
                    displayNowIndicator: true,
                    getBusinessHours: _ctx.currentView === _unref(CalendarView).DAY && getBusinessHour.value,
                    displayCreateAppointment: true,
                    patient: getCurrentPatient(),
                    displayInitial: true,
                    getAppointmentmentTypes: true,
                    doctors: _ctx.doctors,
                    selectedDoctorAppoitmentTypes: selectedDoctorAppoitmentTypes.value,
                    changeDate: props.changeDate + _unref(PlanningRefreshSource).DayAgendaTypeChangeModifier,
                    changeAgendaType: _ctx.changeAgendaType + _unref(PlanningRefreshSource).DayAgendaTypeChangeModifier,
                    changeAppointmentType: changeAppointmentType.value,
                    changePatient: changePatient.value,
                    changeDoctor: _ctx.changeDoctor,
                    changeDoctorList: _ctx.changeDoctorList,
                    eventDurationEditable: false,
                    droppable: false,
                    lastSelectedDoctorId: _ctx.lastSelectedDoctorId,
                    onNavigationChanged: _cache[0] || (_cache[0] = ($event: any) => (emits('refreshCalendar'))),
                    onRefreshed: _cache[1] || (_cache[1] = ($event: any) => (emits('refreshed', {newValue: false, newValueCalendar: false}))),
                    onOpenAddForm: _cache[2] || (_cache[2] = ($event: any) => (openAddForm($event))),
                    onSelectAppointmentType: _cache[3] || (_cache[3] = ($event: any) => (selectAppointmentType($event))),
                    onEventClick: _cache[4] || (_cache[4] = ($event: any) => (openUpdateEvent($event))),
                    onRefreshCalendar: _cache[5] || (_cache[5] = ($event: any) => (emits('refreshed', {newValue: true, newValueCalendar: true}))),
                    onPatientTagClick: _cache[6] || (_cache[6] = ($event: any) => (patientTagClick()))
                  }, null, 8, ["itemTags", "selectedDate", "refresh", "getBusinessHours", "patient", "doctors", "selectedDoctorAppoitmentTypes", "changeDate", "changeAgendaType", "changeAppointmentType", "changePatient", "changeDoctor", "changeDoctorList", "lastSelectedDoctorId"]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["active"]),
          _createVNode(Tab, {
            active: activeTab.value === 1
          }, {
            default: _withCtx(() => [
              (_unref(appUserStore).reactiveCurrentAgendaType === '1')
                ? (_openBlock(), _createBlock(EsWeeklyCalendar, {
                    key: 0,
                    patient: getCurrentPatient(),
                    doctors: _ctx.doctors,
                    getEvents: getEvents,
                    refresh: _ctx.refresh,
                    selectedDate: _ctx.selectedDate,
                    getBusinessHours: _ctx.currentView === _unref(CalendarView).WEEK && getBusinessHour.value,
                    selectedDoctorAppoitmentTypes: selectedDoctorAppoitmentTypes.value,
                    changeDate: props.changeDate + _unref(PlanningRefreshSource).WeekAgendaTypeChangeModifier,
                    changeAgendaType: _ctx.changeAgendaType + _unref(PlanningRefreshSource).WeekAgendaTypeChangeModifier,
                    changeAppointmentType: changeAppointmentType.value,
                    changePatient: changePatient.value,
                    changeDoctor: _ctx.changeDoctor,
                    changeDoctorList: _ctx.changeDoctorList,
                    onRefreshed: _cache[7] || (_cache[7] = ($event: any) => (emits('refreshed', {newValue: false, newValueCalendar: false}))),
                    onSelectAppointmentType: _cache[8] || (_cache[8] = ($event: any) => (selectAppointmentType($event))),
                    onOpenAddForm: _cache[9] || (_cache[9] = ($event: any) => (openAddForm($event))),
                    onSwitchOpened: _cache[10] || (_cache[10] = ($event: any) => (emits('switchOpened', $event))),
                    onSaveLastDoctorOpened: _cache[11] || (_cache[11] = ($event: any) => (emits('saveLastDoctorOpened', $event))),
                    onOpenUpdateEvent: _cache[12] || (_cache[12] = ($event: any) => (openUpdateEvent($event))),
                    onRefreshCalendar: _cache[13] || (_cache[13] = ($event: any) => (emits('refreshed', {newValue: true, newValueCalendar: true}))),
                    onPatientTagClick: _cache[14] || (_cache[14] = ($event: any) => (patientTagClick())),
                    onOpenSettings: _cache[15] || (_cache[15] = ($event: any) => (openSettings($event)))
                  }, null, 8, ["patient", "doctors", "refresh", "selectedDate", "getBusinessHours", "selectedDoctorAppoitmentTypes", "changeDate", "changeAgendaType", "changeAppointmentType", "changePatient", "changeDoctor", "changeDoctorList"]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["active"])
        ]),
        _: 1
      }, 8, ["titles", "selected-date"])
    ]),
    _createVNode(EsCalendarForm, {
      isOpen: isFormModalOpened.value,
      isAllDayEvent: isAllDayEvent.value,
      "selected-date": addAppointmentDate.value,
      header: headerForm.value,
      doctors: _ctx.doctors,
      selectedDoctor: selectedDoctor.value,
      selectedAppointmentType: selectedDoctor.value ? selectedDoctorAppoitmentTypes.value[selectedDoctor.value.id] : null,
      patient: getCurrentPatient(),
      displayDelete: displayDelete.value,
      "delete-method": () => deleteAppointment(),
      "display-move-button": !getIsMoveAppointmentAction.value && !!getCurrentEvent.value,
      "confirm-method": async (appointmentRequest) => await closeFormModal(appointmentRequest),
      onModalClose: closeModal,
      onMoveAppointment: _cache[20] || (_cache[20] = ($event: any) => (moveAppointment($event)))
    }, null, 8, ["isOpen", "isAllDayEvent", "selected-date", "header", "doctors", "selectedDoctor", "selectedAppointmentType", "patient", "displayDelete", "delete-method", "display-move-button", "confirm-method"]),
    (isSettingOpened.value)
      ? (_openBlock(), _createBlock(EsCalendarSettings, {
          key: 0,
          isOpen: isSettingOpened.value,
          selectedDate: dateForSetting.value,
          header: _unref(t)('calendar.label.settings.header', {date: _unref(format)(new Date(dateForSetting.value), _unref(DateFormat).FRONT_LETTER, {locale: local.value})}),
          "selected-doctor": selectedDoctor.value,
          onModalClose: _cache[21] || (_cache[21] = ($event: any) => (closeCalendarSettingsModal($event)))
        }, null, 8, ["isOpen", "selectedDate", "header", "selected-doctor"]))
      : _createCommentVNode("", true),
    _createVNode(EsConfirmDialog, {
      isOpen: isConfirmAppointmentOpened.value,
      header: _unref(t)('calendar.label.existAppointment.header'),
      message: getAppointmentId.value ? _unref(t)('calendar.label.existAppointment.message.update') : _unref(t)('calendar.label.existAppointment.message.create'),
      "on-confirm": () => closeConfirmModal(true),
      onModalClose: _cache[22] || (_cache[22] = ($event: any) => (closeConfirmModal(false)))
    }, null, 8, ["isOpen", "header", "message", "on-confirm"])
  ], 64))
}
}

})